<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { fade, fly } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import FinalCTA from '$lib/components/sections/FinalCTA.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.

	onMount(() => {
		visible = true;
	});

	let values = $derived([
		{
			icon: 'target',
			title: $_('about.values.focus.title'),
			description: $_('about.values.focus.description')
		},
		{
			icon: 'handshake',
			title: $_('about.values.teams.title'),
			description: $_('about.values.teams.description')
		},
		{
			icon: 'sparkles',
			title: $_('about.values.simplicity.title'),
			description: $_('about.values.simplicity.description')
		},
		{
			icon: 'lock',
			title: $_('about.values.privacy.title'),
			description: $_('about.values.privacy.description')
		},
		{
			icon: 'rocket',
			title: $_('about.values.innovation.title'),
			description: $_('about.values.innovation.description')
		},
		{
			icon: 'globe',
			title: $_('about.values.global.title'),
			description: $_('about.values.global.description')
		}
	]);

	let milestones = $derived([
		{ year: '2021', event: $_('about.timeline.2021.description') },
		{ year: '2022', event: $_('about.timeline.2022.description') },
		{ year: '2023', event: $_('about.timeline.2023.description') },
		{ year: '2024', event: $_('about.timeline.2024.description') },
		{ year: '2025', event: $_('about.timeline.2025.description') }
	]);
</script>

<svelte:head>
	<title>{$_('about.meta.title')} | Heyweek</title>
	<meta name="description" content={$_('about.meta.description')} />
	<meta property="og:title" content="{$_('about.meta.title')} | Heyweek" />
	<meta property="og:description" content={$_('about.meta.description')} />
</svelte:head>

<Header />

<div class="about-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			{#if visible}
				<div class="hero__content" in:fade={{ duration: 600, delay: 100 }}>
					<div class="hero__badge" in:fly={{ y: 20, duration: 500, delay: 200 }}>
						<span>{$_('about.hero.badge')}</span>
					</div>
					<h1 class="hero__title" in:fly={{ y: 30, duration: 600, delay: 300 }}>
						{$_('about.hero.title')}
					</h1>
					<p class="hero__subtitle" in:fly={{ y: 30, duration: 600, delay: 400 }}>
						{$_('about.hero.subtitle')}
					</p>
				</div>
			{/if}
		</div>
	</section>

	<!-- Story Section -->
	<section class="story">
		<div class="container">
			<div class="story__grid">
				<div class="story__content">
					<h2 class="story__title">{$_('about.story.title')}</h2>
					<div class="story__text">
						<p>{$_('about.story.paragraph1')}</p>
						<p>{$_('about.story.paragraph2')}</p>
						<p>{$_('about.story.paragraph3')}</p>
					</div>
				</div>
				<div class="story__visual">
					<div class="story__stats">
						<div class="stat">
							<div class="stat__number">2021</div>
							<div class="stat__label">{$_('about.story.stats.founded')}</div>
						</div>
						<div class="stat">
							<div class="stat__number">100%</div>
							<div class="stat__label">{$_('about.story.stats.bootstrapped')}</div>
						</div>
						<div class="stat">
							<div class="stat__number">24/7</div>
							<div class="stat__label">{$_('about.story.stats.improving')}</div>
						</div>
						<div class="stat">
							<div class="stat__number">∞</div>
							<div class="stat__label">{$_('about.story.stats.passion')}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Mission Section -->
	<section class="mission">
		<div class="container">
			<div class="mission__content">
				<h2 class="mission__title">{$_('about.mission.title')}</h2>
				<p class="mission__statement">
					{$_('about.mission.statement')}
				</p>
			</div>
		</div>
	</section>

	<!-- Values Section -->
	<section class="values">
		<div class="container">
			<div class="values__header">
				<h2 class="values__title">{$_('about.values.title')}</h2>
				<p class="values__subtitle">{$_('about.values.subtitle')}</p>
			</div>
			<div class="values__grid">
				{#each values as value, index}
					<div class="value-card">
						<div class="value-card__icon">
							<Icon name={value.icon} size={48} class="text-accent-primary" />
						</div>
						<h3 class="value-card__title">{value.title}</h3>
						<p class="value-card__description">{value.description}</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Timeline Section -->
	<section class="timeline">
		<div class="container">
			<h2 class="timeline__title">{$_('about.timeline.title')}</h2>
			<div class="timeline__content">
				{#each milestones as milestone, index}
					<div class="milestone">
						<div class="milestone__year">{milestone.year}</div>
						<div class="milestone__dot"></div>
						<div class="milestone__event">{milestone.event}</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<FinalCTA backgroundVariant="gradient" />
</div>

<SeoContent slug="about" />

<Footer />

<style>
	.about-page {
		min-height: 100vh;
		padding-top: var(--space-8);
	}

	/* Hero Section */
	.hero {
		text-align: center;
		padding: var(--space-16) 0;
		background: linear-gradient(180deg, var(--bg-secondary) 0%, transparent 100%);
	}

	.hero__content {
		max-width: 900px;
		margin: 0 auto;
	}

	.hero__badge {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-1) var(--space-3);
		background: var(--accent-primary-muted);
		color: var(--accent-primary);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
	}

	.hero__title {
		font-size: clamp(3rem, 6vw, 4.5rem);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.03em;
	}

	.hero__subtitle {
		font-size: var(--font-size-xl);
		color: var(--text-secondary);
		line-height: 1.6;
		max-width: 700px;
		margin: 0 auto;
	}

	/* Story Section */
	.story {
		padding: var(--space-20) 0;
	}

	.story__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
		max-width: 1200px;
		margin: 0 auto;
	}

	.story__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
	}

	.story__text {
		color: var(--text-secondary);
		line-height: 1.8;
		font-size: var(--font-size-lg);
	}

	.story__text p {
		margin-bottom: var(--space-4);
	}

	.story__visual {
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.story__stats {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-8);
		padding: var(--space-8);
		background: var(--bg-secondary);
		border-radius: var(--radius-xl);
		border: 1px solid var(--border-light);
	}

	.stat {
		text-align: center;
	}

	.stat__number {
		font-size: var(--font-size-4xl);
		font-weight: var(--font-weight-bold);
		color: var(--accent-primary);
		margin-bottom: var(--space-2);
	}

	.stat__label {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	/* Mission Section */
	.mission {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
		border-radius: var(--radius-xl);
		margin: 0 var(--space-4);
	}

	.mission__content {
		text-align: center;
		max-width: 900px;
		margin: 0 auto;
	}

	.mission__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
	}

	.mission__statement {
		font-size: var(--font-size-2xl);
		color: var(--text-primary);
		line-height: 1.6;
		font-weight: var(--font-weight-light);
	}

	/* Values Section */
	.values {
		padding: var(--space-20) 0;
	}

	.values__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.values__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.values__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	.values__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: var(--space-6);
		max-width: 1200px;
		margin: 0 auto;
	}

	.value-card {
		padding: var(--space-8);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		transition: all 0.3s ease;
	}

	.value-card:hover {
		transform: translateY(-4px);
		box-shadow: var(--shadow-elevated);
		border-color: var(--accent-primary);
	}

	.value-card__icon {
		display: flex;
		justify-content: center;
		margin-bottom: var(--space-4);
	}

	.value-card__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-3);
	}

	.value-card__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* Timeline Section */
	.timeline {
		padding: var(--space-20) 0;
		overflow: hidden;
	}

	.timeline__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.timeline__content {
		position: relative;
		max-width: 900px;
		margin: 0 auto;
		padding: 0 var(--space-8);
	}

	.timeline__content::before {
		content: '';
		position: absolute;
		left: 120px;
		top: 0;
		bottom: 0;
		width: 2px;
		background: linear-gradient(180deg, var(--accent-primary), var(--accent-secondary));
		opacity: 0.3;
	}

	.milestone {
		display: flex;
		align-items: center;
		gap: var(--space-6);
		margin-bottom: var(--space-8);
		position: relative;
	}

	.milestone__year {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-bold);
		color: var(--accent-primary);
		width: 80px;
		text-align: right;
	}

	.milestone__dot {
		width: 16px;
		height: 16px;
		background: var(--accent-primary);
		border-radius: 50%;
		position: relative;
		z-index: 1;
		box-shadow: 0 0 0 4px var(--bg-primary);
	}

	.milestone__event {
		flex: 1;
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
	}


	/* Responsive Design */
	@media (max-width: 968px) {
		.story__grid {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}

		.story__stats {
			max-width: 500px;
			margin: 0 auto;
		}
	}

	@media (max-width: 768px) {
		.hero__title {
			font-size: 2.5rem;
		}

		.values__grid {
			grid-template-columns: 1fr;
		}

		.timeline__content::before {
			left: 20px;
		}

		.milestone {
			flex-direction: column;
			align-items: flex-start;
			padding-left: var(--space-12);
		}

		.milestone__year {
			text-align: left;
			width: auto;
		}

		.milestone__dot {
			position: absolute;
			left: 12px;
		}
	}
</style>